// frontend/src/features/forms/pages/FormFillPage.tsx
// AM-006: پشتیبانی از Entity Binding، Chain Rules، Header Inheritance
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  useV1FormsFormRevisionsRetrieve,
  useV1FormsFormInstanceDraftsCreate,
  useV1FormsFormInstanceDraftsPartialUpdate,
  useV1FormsFormInstanceDraftsSubmitCreate,
  useV1FormsFormInstanceDraftsRetrieve,
  v1FormsFormFamiliesList,
  v1FormsFormRevisionsList,
} from "@/api/generated/v1/v1";
import { useAuthStore } from "@/auth/authStore";
import { DynamicFormRenderer } from "../components/DynamicFormRenderer";
import type { ChainRule, FormFieldDef } from "@/shared/utils/formSchema";
import { EntityPicker } from "@/shared/components/EntityPicker";
import { PageBanner } from "@/shared/components/PageBanner";

/** AM-006 AM-03: ارزیابی قانون اگر-آنگاه روی مقدار یک فیلد */
function evaluateChainRule(rule: ChainRule, value: any): boolean {
  if (value === undefined || value === null || value === "") return false;
  switch (rule.operator) {
    case "eq":
      return String(value) === String(rule.value);
    case "lt":
      return Number(value) < Number(rule.value);
    case "lte":
      return Number(value) <= Number(rule.value);
    case "gt":
      return Number(value) > Number(rule.value);
    case "gte":
      return Number(value) >= Number(rule.value);
    case "between": {
      const [min, max] = rule.value as [number, number];
      return Number(value) >= Number(min) && Number(value) <= Number(max);
    }
    case "in":
      return (rule.value as string[]).map(String).includes(String(value));
    default:
      return false;
  }
}

function findAllFields(def: any): FormFieldDef[] {
  const sections = [def?.header, def?.body, def?.footer].filter(Boolean);
  return sections.flatMap((s) => s.fields || []);
}

export function FormFillPage() {
  const { revisionId } = useParams<{ revisionId: string }>();
  const [searchParams] = useSearchParams();
  const parentDraftId = searchParams.get("parentDraftId");
  const navigate = useNavigate();
  const currentUserId = useAuthStore((s) => s.user?.id);

  const { data: revision, isLoading } = useV1FormsFormRevisionsRetrieve(revisionId!);
  const { data: parentDraft } = useV1FormsFormInstanceDraftsRetrieve(parentDraftId || "", {
    query: { enabled: !!parentDraftId },
  } as any);

  const { mutateAsync: createDraft } = useV1FormsFormInstanceDraftsCreate();
  const { mutateAsync: patchDraft } = useV1FormsFormInstanceDraftsPartialUpdate();
  const { mutateAsync: submitDraft, isPending: isSubmitting } = useV1FormsFormInstanceDraftsSubmitCreate();

  const [payload, setPayload] = useState<Record<string, any>>({});
  const [draftId, setDraftId] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inheritedAppliedRef = useRef(false);

  // AM-006 AM-03-R4: ارثبری خودکار فیلدهای هدر از فرم مبدأ
  useEffect(() => {
    if (parentDraft && revision?.definition_json && !inheritedAppliedRef.current) {
      const def: any = revision.definition_json;
      const inheritKeys: string[] = def.inherit_from_parent || [];
      if (inheritKeys.length > 0) {
        const parentPayload = (parentDraft as any).payload || {};
        const inherited: Record<string, any> = {};
        for (const key of inheritKeys) {
          if (key in parentPayload) inherited[key] = parentPayload[key];
        }
        if (Object.keys(inherited).length > 0) {
          setPayload((p) => ({ ...inherited, ...p }));
        }
      }
      inheritedAppliedRef.current = true;
    }
  }, [parentDraft, revision]);

  // AM-006 binding: انتخاب موجودیت مقصد
  const binding = (revision?.definition_json as any)?.binding;
  const [selectedModelKey, setSelectedModelKey] = useState("");
  const [target, setTarget] = useState<{ id: string; display: string } | null>(null);
  const bindingSatisfied = !binding?.required || !!target;

  const persistDraft = async (nextPayload: Record<string, any>) => {
    if (!currentUserId || !bindingSatisfied) return;
    setStatus("saving");
    try {
      if (!draftId) {
        const res = await createDraft({
          data: {
            revision: revisionId!,
            user_id: currentUserId,
            payload: nextPayload,
            ...(binding?.required ? { target_model: selectedModelKey, target_object_id: target!.id } : {}),
            ...(parentDraftId ? { parent_draft: parentDraftId } : {}),
          } as any,
        });
        setDraftId((res as any).id);
      } else {
        await patchDraft({ id: draftId, data: { payload: nextPayload } as any });
      }
      setStatus("saved");
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || JSON.stringify(err.response?.data) || "خطا در ذخیرهٔ پیشنویس");
    }
  };

  // AM-006 AM-03: بعد از تغییر مقدار، chain_rules را چک کن
  const handleChainCheck = async (field: FormFieldDef, value: any) => {
    if (!field.chain_rules?.length) return;
    const matched = field.chain_rules.find((r) => evaluateChainRule(r, value));
    if (!matched) return;

    try {
      const families = await v1FormsFormFamiliesList({ code: matched.target_family_code } as any);
      const family = (families as any).results?.[0];
      if (!family) {
        alert(`فرم مقصد با کد ${matched.target_family_code} پیدا نشد.`);
        return;
      }
      const revisions = await v1FormsFormRevisionsList({ family: family.id, status: "published" } as any);
      const list = (revisions as any).results || [];
      const latest = list.sort((a: any, b: any) => b.revision_no - a.revision_no)[0];
      if (!latest) {
        alert(`فرم "${matched.target_family_code}" هیچ نسخهٔ منتشرشدهای ندارد.`);
        return;
      }
      if (!draftId) {
        alert("قبل از رفتن به فرم بعدی، حداقل یک فیلد را تکمیل کنید تا پیشنویس ذخیره شود.");
        return;
      }
      if (confirm(`طبق مقدار واردشده، فرم "${matched.target_family_code}" باید تکمیل شود. الان بروید؟`)) {
        navigate(`/forms/fill/${latest.id}?parentDraftId=${draftId}`);
      }
    } catch {
      alert("خطا در یافتن فرم بعدی زنجیره.");
    }
  };

  const handleChange = (key: string, value: any) => {
    const next = { ...payload, [key]: value };
    setPayload(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => persistDraft(next), 800);

    const allFields = findAllFields(revision?.definition_json);
    const field = allFields.find((f) => f.key === key);
    if (field) handleChainCheck(field, value);
  };

  // AM-006: Auto-populate payload from target entity if fields define 'source'
  useEffect(() => {
    if (target && selectedModelKey && revision?.definition_json) {
      const allFields = findAllFields(revision.definition_json);
      const fieldsWithSource = allFields.filter(f => f.source);
      
      if (fieldsWithSource.length > 0) {
        import("@/api/client").then(({ axiosInstance }) => {
          axiosInstance.get("/api/v1/shared/entity-search/detail/", {
            params: { model: selectedModelKey, id: target.id }
          }).then(res => {
            const data = res.data;
            const updates: Record<string, any> = {};
            
            for (const field of fieldsWithSource) {
              const srcPath = field.source!;
              // Very simple lodash.get-like resolver
              const parts = srcPath.split(".");
              // typically source is like "material.custom_fields.packing_image"
              // In the generic response, 'data' is the material object itself. 
              // So parts[0] == "material" is just a namespace. Let's start from parts[1] if parts[0] is "material"
              let current = data;
              let pathParts = parts;
              if (parts.length > 1 && parts[0] === "material") {
                 pathParts = parts.slice(1);
              }
              for (const p of pathParts) {
                if (current && typeof current === "object" && p in current) {
                  current = current[p];
                } else {
                  current = undefined;
                  break;
                }
              }
              if (current !== undefined) {
                updates[field.key] = current;
              }
            }
            
            if (Object.keys(updates).length > 0) {
              setPayload(p => ({ ...p, ...updates }));
            }
          }).catch(() => {
            // ignore fetch errors
          });
        });
      }
    }
  }, [target, selectedModelKey, revision]);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const handleSubmit = async () => {
    if (!draftId) {
      alert("ابتدا حداقل یک فیلد را تکمیل کنید.");
      return;
    }
    try {
      await submitDraft({ id: draftId, data: {} as any });
      alert("فرم با موفقیت ثبت شد.");
      navigate("/forms/instances");
    } catch (err: any) {
      alert(err.response?.data?.detail || JSON.stringify(err.response?.data) || "خطا در ثبت نهایی فرم — فیلدهای اجباری را بررسی کنید.");
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 flex-center">
        <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری...</p>
      </div>
    );
  }

  if (revision?.status !== "published") {
    return (
      <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        <PageBanner
          title="خطا: فرم در دسترس نیست"
          subtitle="این نسخه از فرم هنوز منتشر نشده است"
          icon="⚠️"
          helpLink="/help/forms/form-fill"
        />
        <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
          <p style={{ color: "var(--color-error)" }}>این نسخه از فرم منتشر نشده و قابل تکمیل نیست.</p>
        </div>
      </div>
    );
  }

  const def: any = revision.definition_json;

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title={def.title}
        subtitle="تکمیل و ثبت اطلاعات فرم"
        icon="📝"
        helpLink="/help/forms/form-fill"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
            {status === "saving" && "در حال ذخیرهٔ پیشنویس..."}
            {status === "saved" && "پیشنویس ذخیره شد ✓"}
            {status === "error" && "خطا در ذخیرهٔ پیشنویس"}
          </span>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "900px", margin: "0 auto", padding: "2rem", width: "100%" }}>

        {parentDraftId && (
          <div className="p-3 mb-4" style={{ background: "rgba(186,153,96,0.1)", borderRight: "3px solid var(--color-gold)", borderRadius: "6px" }}>
            <p style={{ fontSize: "0.85rem", color: "var(--color-gold)" }}>
              این فرم بخشی از یک زنجیرهٔ فرم است — برخی فیلدها خودکار از فرم قبلی پر شدهاند.
            </p>
          </div>
        )}

        {errorMsg && (
          <div className="p-4 mb-4" style={{ background: "rgba(255,0,0,0.1)", borderLeft: "4px solid red", borderRadius: "8px" }}>
            <p style={{ color: "red" }}>{errorMsg}</p>
          </div>
        )}

        {binding?.required && (
          <div className="brand-card mb-6" style={{ padding: "1.25rem", position: "relative", zIndex: 10 }}>
            <h3 className="font-bold mb-3" style={{ fontSize: "0.9rem" }}>اتصال به موجودیت (اجباری)</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", position: "relative" }}>
              <select className="input-field" dir="rtl" value={selectedModelKey} disabled={!!draftId}
                onChange={(e) => { setSelectedModelKey(e.target.value); setTarget(null); }}>
                <option value="">-- نوع موجودیت --</option>
                {(binding.allowed_models || []).map((m: string) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {selectedModelKey && (
                <EntityPicker
                  model={selectedModelKey}
                  value={target}
                  onChange={setTarget}
                  disabled={!!draftId}
                  placeholder="کد یا نام را تایپ کنید..."
                />
              )}
            </div>
            {!bindingSatisfied && (
              <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", marginTop: "0.5rem" }}>
                ابتدا نوع و مقدار موجودیت را انتخاب کنید تا بتوانید فرم را پر کنید.
              </p>
            )}
          </div>
        )}

        {bindingSatisfied && (
          <>
            <DynamicFormRenderer definition={def} value={payload} onChange={handleChange} />
            <div className="flex-end mt-6">
              <button className="btn btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "در حال ثبت..." : "ثبت نهایی فرم"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
